import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {reservationStatus, toCamelCase, formatCurrency, constants} from "../../services/utilities";
import {
  mergeReservationEventOverride,
  toastOrderQueryWhere,
  unwrapImportResultsResponse
} from "../../services/reservationImport";
import { getDocument, createDocument, getCollection, timestamp, fieldDelete, fieldDocumentId, api } from "../config";
import {
  toEmailActivityError,
  EMAIL_DETAIL_UNAVAILABLE_MESSAGE,
  EMAIL_RESEND_UNAVAILABLE_MESSAGE
} from "../../services/emailActivity";
import moment from "moment-timezone";

export const loadReservation = createAsyncThunk(
    "reservation/loadReservation",
    async (payload, { rejectWithValue }) => {
        try{
            let rez = await getDocument("event_reservations", payload, [
                {
                  key: "eventUserID",
                  collection: "users_public",
                  field: "eventUserPublic",
                },
                {
                  key: "id",
                  collection: "event_reservation_payments",
                  field: "erp",
                },
                {
                  key: "userID",
                  collection: "users",
                  field: "user",
                },
                {
                  key: "orgID",
                  collection: "user_organizations",
                  field: "org",
                },
                // `eventID -> event` is deliberately NOT in this list — see the merge
                // immediately after the call.
                {
                  key: "locationID",
                  collection: "locations",
                  field: "location",
                },
                {
                  key: "brandID",
                  collection: "brands",
                  field: "brand",
                },
            ]);

            // ISSUE #421 — the reservation's own `event` map is an OVERRIDE snapshot, not
            // a cache of the event type: apiEventReservationPayoutUpdated writes the
            // per-reservation `payout`/`payoutType` into it. `getDocument`'s join
            // REPLACES the field wholesale (store/config.js:257), so joining `events`
            // here silently discarded that override — and results.js computes
            // `sales * (reservation.event.payout/100)`, so the Confirm Results page
            // showed a donation at the event type's rate instead of the reservation's.
            // Same defect the backend carried in utilities/common.js:getReservationDetails.
            // Same read count as the join it replaces.
            if(rez?.eventID){
              const eventType = await getDocument("events", rez.eventID);
              if(eventType) rez.event = mergeReservationEventOverride(eventType, rez.event);
            }

            if(rez.eventUserPublic?.skipDocumentsIfVerified&&rez.org?.taxid&&rez.org?.taxidverified){
              rez.irsOrg = await getDocument("organizations", rez.org.taxid);
            }

            if(rez.groupID) {

              const group = await getDocument("groups", rez.groupID);
              
              if(group) {

                rez.location = {
                  location:group.name,
                  address1:group.name,
                  timezone:moment.tz.guess()
                }
                const batchSize = 30;
                const batches = [];
                
                // Split locationIDs into batches of 30
                for (let i = 0; i < group.locationIDs.length; i += batchSize) {
                  batches.push(group.locationIDs.slice(i, i + batchSize));
                }
                
                // Execute queries for each batch in parallel
                const batchPromises = batches.map(batch => 
                  getCollection("locations", {
                    where: [
                      [fieldDocumentId(), "in", batch]
                    ]
                  })
                );
                
                // Wait for all queries and flatten results
                const batchResults = await Promise.all(batchPromises);
                rez.locations = batchResults.flat();
                rez.location.address1 = rez.locations.slice(0, 10).map(l=>l.location).join(', ');
                if(rez.locations.length > 10){
                  rez.location.address1 += ` and ${rez.locations.length - 10} more...`;
                }
              }
              
              
            }

            return rez;
        }catch(e){
            return rejectWithValue(e.message);
        }
    }
);

export const getPurchases = createAsyncThunk(
  "reservation/getPurchases",
  async (_, { getState, rejectWithValue }) => {
      try{
        const {reservation} = getState();
        let rs = await getCollection(`event_reservations/${reservation.reservation.id}/purchases`, {order:['created','desc']});
        return rs;
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const loadEmail = createAsyncThunk(
  "reservation/loadEmail",
  async (MessageID, { rejectWithValue }) => {
      
    try{

      const response = await api.postmarkDetail({
        message_id:MessageID
      });
      
      const {message} = response.data.data;

      return message;

    }catch(e){

      console.warn('loadEmail failed', e?.code, e?.message);

      return rejectWithValue(toEmailActivityError(e,EMAIL_DETAIL_UNAVAILABLE_MESSAGE));

    }
  }
);

export const resendEmail = createAsyncThunk(
  "reservation/resendEmail",
  async (_, { getState, rejectWithValue }) => {
      
    try{

      const {reservation} = getState();
      
      const Message = reservation.emailToResend;
      
      const response = await api.postmarkResend({
        message_id:Message.MessageID,
        recipient:Message.Recipient
      });
      
      const {email} = response.data.data;

      return email;

    }catch(e){

      console.warn('resendEmail failed', e?.code, e?.message);

      return rejectWithValue(toEmailActivityError(e,EMAIL_RESEND_UNAVAILABLE_MESSAGE));

    }
  }
);

export const loadEmails = createAsyncThunk(
  "reservation/loadEmails",
  async (_, { getState, rejectWithValue }) => {
      
    try{

      const {reservation} = getState();
      
      const response = await api.postmarkOutbound({
        reservation_id:reservation.reservation.id
      });
      
      const {messages} = response.data.data;

      return messages;

    }catch(e){

      console.warn('loadEmails failed', e?.code, e?.message);

      return rejectWithValue(toEmailActivityError(e));

    }
  }
);

export const refreshCreative = createAsyncThunk(
  "reservation/refreshCreative",
  async (_, { getState, rejectWithValue }) => {
      
    try{

        const {reservation} = getState();
          
        let dispatchData = {
            reservationID:reservation.reservation.id
        };
        await api.brandCreativeRefresh(dispatchData)

        return;

      }catch(e){

          return rejectWithValue(e.message);

      }
  }
);

export const changeStatus = createAsyncThunk(
  "reservation/changeStatus",
  async (payload, { getState, rejectWithValue }) => {
      try{
        const {account,reservation} = getState();
        
        let status = toCamelCase(payload.status);

        let data = {
          reservationID:reservation.reservation.id,
          ['updateBy']:account.user.parentUserID?account.user.parentUserID:account.user.uid
        };

        if((status=='cancelled'||status=='denied')){
          data['reason']=payload.reason||'';
        }

        switch(status){
          case'cancelled':
            await api.eventReservationCancel(data);
          break;
          case'denied':
            await api.eventReservationDeny(data);
          break;
          case'approved':
            await api.eventReservationApprove(data);
          break;
          case'missingInfo':
            await api.eventReservationMissingInfo(data);
          break;
        }

        return {
          status : {[status]:true},
          [status+'By']:account.user.parentUserID?account.user.parentUserID:account.user.uid
        };
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const saveResults = createAsyncThunk(
  "reservation/saveResults",
  async (payload, { getState, rejectWithValue }) => {
      try{
        const {reservation} = getState();
        
        let data = {...payload};

        if(reservation.messages.filter(m=>m.message==='Results Posted').length>0){
          await api.eventReservationResultsUpdate({
            reservationID:reservation.reservation.id,
            updateBy:reservation.reservation.eventUserID,
            updateType:'results',
            updateFrom:{
              sales:'$'+formatCurrency(reservation.reservation.sales),
              donation:'$'+formatCurrency(reservation.reservation.donation),
              threshold:(reservation.reservation.thresholdNotMet?'No':'Yes')
            },
            updateTo:{
              sales:'$'+formatCurrency(data.sales),
              donation:'$'+formatCurrency(data.donation),
              threshold:(payload.thresholdNotMet?'No':'Yes')
            },
            ...data
          });
        }else{
          await api.eventReservationResults({
            reservationID:reservation.reservation.id,
            updateBy:reservation.reservation.eventUserID,
            ...data
          });
        }

        return {...payload};
      }catch(e){
        console.log(e.message)
          return rejectWithValue(e.message);
      }
  }
);

export const savePayment = createAsyncThunk(
  "reservation/savePayment",
  async (payload, { getState, rejectWithValue }) => {
      try{
        const {reservation} = getState();
        await api.apiEventReservationPayment({
          reservationID:reservation.reservation.id,
          updateBy:reservation.reservation.eventUserID,
          ...payload
        })
        return {...payload};
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const changeEventType = createAsyncThunk(
  "reservation/changeEventType",
  async (payload, { getState, rejectWithValue }) => {
      try{
        const {account,reservation} = getState();
        console.log(payload);
        await api.eventReservationChangeEventType({
          reservationID:reservation.reservation.id,
          updateBy:account.user.uid,
          eventID:payload.id
        })
        return {...payload};
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const changeDate = createAsyncThunk(
  "reservation/changeDate",
  async (payload, { getState, rejectWithValue }) => {
      try{
        const {account,reservation} = getState();
        
        await api.eventReservationReschedule({
          ...payload.updateData,
          reservationID:reservation.reservation.id,
          updateBy:(account.user.permissions.admin?reservation.reservation.eventUserID:(account.user.parentUserID?account.user.parentUserID:account.user.uid)),
          updateType:'date',
          updateFrom:payload.updateFrom,
          updateTo:payload.updateTo,
          reason:payload.reason
        });

        return payload;
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const loadLocations = createAsyncThunk(
  "reservation/loadLocations",
  async (_, { getState, rejectWithValue }) => {
      try{
        const {account,reservation} = getState();
        
        let rs = await getCollection("locations", {
          where: [
            ["brandID", "==", reservation.reservation.brandID]
          ]
        });
        return rs.filter(l=>!l.deleted&&!["Non Participating","Temporarily Closed","Scheduled","Referral"].includes(l.status))
        .filter(l=>account.user.permissions.admin||l.users?.[account.user.parentUserID?account.user.parentUserID:account.user.uid])
        .map((l)=>Object.assign({
          value:l.id,
          label:(reservation.reservation.brand.includeExternal&&l.externalID?l.externalID + ' - ':'') + l.location,
          location:l.location,
          address1:l.address1,
          city:l.city,
          state:l.state,
          zip:l.zip,
          schedule:l.schedule,
          timezone:l.timezone
        }))
        .sort((a,b) => (a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0));
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const loadEventTypes = createAsyncThunk(
  "reservation/loadEventTypes",
  async (_, { getState, rejectWithValue }) => {
      try{
        const {account,reservation} = getState();

        let rs = await getCollection("events", {
          where: [
            ["brandID", "==", reservation.reservation.brandID],
            ["userID", "==", account.user.parentUserID||account.user.uid]
          ]
        });
        return rs.filter((e)=>(e.active&&!e.multiDay&&(e.default||Object.keys(e.locationIDs||{}).includes(reservation.reservation.locationID)))||e.id==reservation.reservation.eventID).map((e)=>Object.assign({
          value:e.id,
          label:e.name,
          ...e
        }))
        .sort((a,b) => (a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0));
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const loadMessages = createAsyncThunk(
  "reservation/loadMessages",
  async (payload, { getState, rejectWithValue }) => {
      try{
        let rs = await getCollection("messages", {
          where: [
            ["reservationID", "==", payload]
          ],
          order:['date','desc']
        });
        return rs;
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const loadNotes = createAsyncThunk(
  "reservation/loadNotes",
  async (payload, { rejectWithValue }) => {
    try {
      let rs = await getCollection("event_reservation_notes", {
        where: [
          ["reservationID", "==", payload],
          ["deleted", "==", false]
        ],
        order: ['created', 'desc']
      });
      return rs || [];
    } catch(e) {
      return rejectWithValue(e.message);
    }
  }
);

export const saveNote = createAsyncThunk(
  "reservation/saveNote",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const {reservation} = getState();
      const response = await api.reservationNoteCreate({
        reservationID: reservation.reservation.id,
        body: payload.body || '',
        files: payload.files || []
      });
      return { id: response.data.id, ...response.data.data };
    } catch(e) {
      return rejectWithValue(e.message);
    }
  }
);

export const updateNote = createAsyncThunk(
  "reservation/updateNote",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.reservationNoteUpdate({
        noteID: payload.noteID,
        body: payload.body
      });
      return { id: response.data.id, ...response.data.data };
    } catch(e) {
      return rejectWithValue({ noteID: payload.noteID, message: e.message });
    }
  }
);

export const deleteNote = createAsyncThunk(
  "reservation/deleteNote",
  async (noteID, { rejectWithValue }) => {
    try {
      await api.reservationNoteDelete({ noteID });
      return noteID;
    } catch(e) {
      return rejectWithValue({ noteID, message: e.message });
    }
  }
);

export const changeLocation = createAsyncThunk(
  "reservation/changeLocation",
  async (payload, { getState, rejectWithValue }) => {
      try{
        const {account,reservation} = getState();
        
        let newLocation = {...payload.location};
        let updateFrom = {};
        
        ['location','address1','city','state','zip'].forEach(f=>updateFrom[f]=reservation.reservation.location[f]);

        let updateTo = {};
        ['location','address1','city','state','zip'].forEach(f=>updateTo[f]=newLocation[f]);

        let updateData = {};

        newLocation.id=newLocation.value;
        delete newLocation.value;

        if(!reservation.reservation.eventID){
          updateData.location = {...newLocation};
        }else{
          updateData.locationID = newLocation.id;
        }

        await api.eventReservationLocationUpdate({
          reservationID:reservation.reservation.id,
          updateBy:(account.user.permissions.admin?reservation.reservation.eventUserID:(account.user.parentUserID?account.user.parentUserID:account.user.uid)),
          updateType:'location',
          updateFrom:updateFrom,
          updateTo:updateTo,
          reason:payload.reason,
          ...updateData
        });

        return payload;
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const changePayout = createAsyncThunk(
  "reservation/changePayout",
  async (payload, { getState, rejectWithValue }) => {
      try{

        const {account,reservation} = getState();
        
        let updateFrom = reservation.reservation.event.payout + '% of ' + reservation.reservation.event.payoutType;
        let updateTo = payload.payoutChanges.payout + '% of ' + payload.payoutChanges.payoutType;
        
        await api.eventReservationPayoutUpdate({
          reservationID:reservation.reservation.id,
          updateBy:(account.user.permissions.admin?reservation.reservation.eventUserID:(account.user.parentUserID?account.user.parentUserID:account.user.uid)),
          updateType:'payout',
          updateFrom:updateFrom,
          updateTo:updateTo,
          reason:payload.reason,
          payout:payload.payoutChanges.payout,
          payoutType:payload.payoutChanges.payoutType
        });

        return {
          ...reservation.reservation.event,
          payout:payload.payoutChanges.payout,
          payoutType:payload.payoutChanges.payoutType
        };

      }catch(e){
          
        return rejectWithValue(e.message);

      }
  }
);

export const changePaymentTo = createAsyncThunk(
  "reservation/changePaymentTo",
  async (payload, { getState, rejectWithValue }) => {
      try{
        const {reservation} = getState();
        await createDocument({entity:'event_reservation_payments',id:reservation.reservation.id,merge:true},payload.data);
        return payload.data;
      }catch(e){
          return rejectWithValue(e.message);
      }
  }
);

export const fetchOrders = createAsyncThunk(
  "reservation/fetchOrders",
  async (payload, { getState, rejectWithValue }) => {
    try{
      const {reservation} = getState();
      const orders = await getCollection("event_reservation_toast_orders", {
        where: toastOrderQueryWhere(reservation.reservation.id, payload?.locationID)
      });
      return orders?orders.sort((a,b) => (a.opened > b.opened) ? 1 : ((b.opened > a.opened) ? -1 : 0)):[];
    }catch(e){
      return rejectWithValue(e.message);
    }
  }
);

// Per-location results of the automatic multi-location Toast import (issue #421).
//
// A callable rather than a Firestore query: the rows live in
// `event_reservation_import_jobs`, which is server-write / admin-read, and listing ~90 of
// them from the client would cost an ownership rule `get()` per document. The callable
// also folds in locations that never produced a job at all (no Toast mapping), which is
// what lets the page say "80 of 90 locations" instead of quietly totalling 80.
export const loadImportResults = createAsyncThunk(
  "reservation/loadImportResults",
  async (_, { getState, rejectWithValue }) => {
    try{
      const {reservation} = getState();
      const response = await api.getEventReservationImportResults({
        reservationID: reservation.reservation.id
      });
      return unwrapImportResultsResponse(response);
    }catch(e){
      return rejectWithValue(e.message);
    }
  }
);

export const reservationSlice = createSlice({
    name: "reservation",
    initialState: {
      loading:true,
      loadingError:null,
      loadingMessages:true,
      messagesError:null,
      loadingEmails:true,
      loadEmailError:null,
      emailsError:null,
      // Kept separate from `emailsError` on purpose: that field drives a branch which
      // REPLACES the whole activity table, so routing a failed resend into it wiped the
      // list the operator was working from. A resend failure concerns one message.
      resendError:null,
      emails:[],
      emailToResend:null,
      emailToLoad:null,
      reservation:{
        event:null,
        location:null
      },
      orders:[],
      loadingOrders:false,
      ordersError:null,
      // Multi-location Toast import (#421). `null` means "not loaded yet", which is
      // distinct from a loaded response with no locations.
      importResults:null,
      loadingImportResults:false,
      importResultsError:null,
      messages:[],
      editingDate:false,
      changingDate:false,
      changeDateError:null,
      editingLocation:false,
      changingLocation:false,
      changeLocationError:null,
      loadingLocations:false,
      locations:[],
      editingPayout:false,
      editingEventType:false,
      loadingEventTypes:false,
      eventTypes:[],
      changeEventTypeError:null,
      changingPayout:false,
      changePayoutError:null,
      editingPaymentTo:false,
      changingPaymentTo:false,
      changePaymentToError:null,
      activeModal:null,
      changeStatusText:'',
      changingStatus:false,
      changeStatusError:null,
      savingResults:false,
      savingResultsError:null,
      savingPayment:false,
      savingPaymentError:null,
      refreshingCreative:false,
      creativeError:null,
      purchases:[],
      purchasesError:null,
      loadingPurchases:true,
      notes:[],
      loadingNotes:false,
      notesError:null,
      savingNote:false,
      savingNoteError:null,
      updatingNoteID:null,
      updatingNoteError:null,
      deletingNoteID:null,
      deletingNoteError:null
    },
    reducers: {
      setEditingDate: (state,action) => {
        state.editingDate = action.payload;
      },
      setChangeDateError: (state,action) => {
        state.changeDateError = action.payload;
      },
      setEditingLocation: (state,action) => {
        state.editingLocation = action.payload;
      },
      setEditingEventType: (state,action) => {
        state.editingEventType = action.payload;
      },
      setChangeEventTypeError: (state,action) => {
        state.changeEventTypeError = action.payload;
      },
      setChangeLocationError: (state,action) => {
        state.changeLocationError = action.payload;
      },
      setEditingPayout: (state,action) => {
        state.editingPayout = action.payload;
      },
      setChangePayoutError: (state,action) => {
        state.changePayoutError = action.payload;
      },
      setEditingPaymentTo: (state,action) => {
        state.editingPaymentTo = action.payload;
      },
      setChangePaymentToError: (state,action) => {
        state.changePaymentToError = action.payload;
      },
      setActiveModal: (state,action) => {
        state.activeModal = action.payload;
      },
      setChangeStatusText: (state,action) => {
        state.changeStatusText = action.payload;
      },
      setChangeStatusError: (state,action) => {
        state.changeStatusError = action.payload;
      },
      setSavingResultsError: (state,action) => {
        state.savingResultsError = action.payload;
      },
      setSavingPaymentError: (state,action) => {
        state.savingPaymentError = action.payload;
      },
      setMessages: (state,action) => {
        state.messages = action.payload;
      },
      setNotes: (state,action) => {
        state.notes = action.payload;
      },
      setUpdatingNoteError: (state,action) => {
        state.updatingNoteError = action.payload;
      },
      setDeletingNoteError: (state,action) => {
        state.deletingNoteError = action.payload;
      },
      setEmailToResend: (state,action) => {
        state.emailToResend = action.payload;
      },
      setResendError: (state,action) => {
        state.resendError = action.payload;
      },
      setEmailToLoad: (state,action) => {
        state.emailToLoad = action.payload;
      },
      setAchFrom: (state,action) => {
        console.log(action.payload);
        state.reservation.filterStatus = 'Pending Check';
        state.reservation.achFrom = action.payload;
      }
    },
    extraReducers: {
      [loadReservation.fulfilled]: (state, action) => {
        if(action.payload) {

          let shortDate = moment(action.payload.eventDate,constants.dayFormat).format('ll');

          if(action.payload.multiDay) {
        
            const startDate = moment(action.payload.eventDate.split('-').shift(),constants.dayFormat);
            const endDate = moment(action.payload.eventDate.split('-').pop(),constants.dayFormat);

            shortDate = startDate.format('ll') + ' to ' + endDate.format('ll');

          }

          state.reservation = {
            ...action.payload,
            shortDate:shortDate,
            filterStatus:reservationStatus(action.payload)
          };
          // Referral (pending) reservations can lack joined event/brand/location
          // docs (no eventID, or an unseeded brand), which the join helper simply
          // omits. Default them so neither the detail-page components nor the
          // event derefs below crash on undefined.
          state.reservation.brand = state.reservation.brand || {};
          state.reservation.location = state.reservation.location || {};
          state.reservation.event = state.reservation.event || {};
          if(!state.reservation.event.brand) state.reservation.event.brand = state.reservation.brand;
          state.reservation.paymentTo = action.payload.erp?.paymentTo;
          state.reservation.paymentFrom = action.payload.erp?.paymentFrom;
          state.reservation.achFrom = action.payload.erp?.achFrom;
          state.reservation.org = state.reservation.org&&state.reservation.org.id!==state.reservation.id?state.reservation.org:(state.reservation.user.org?state.reservation.user.org:null);
          state.reservation.timezone = state.reservation.location&&state.reservation.location.timezone?state.reservation.location.timezone:moment.tz.guess();
          state.reservation.allDay = ('allDay' in state.reservation) ? state.reservation.allDay : ('allDay' in state.reservation.event && !state.reservation.starts) ? state.reservation.event.allDay : false;
          state.reservation.timeStarts = state.reservation.starts?state.reservation.starts:state.reservation.event.starts;
          state.reservation.timeEnds = state.reservation.ends?state.reservation.ends:state.reservation.event.ends;
          state.reservation.hours = state.reservation.allDay?'All Day':moment(state.reservation.timeStarts).format('h:mm a') + ' to ' + moment(state.reservation.timeEnds).format('h:mm a z');
          state.reservation.isAfter = moment().isAfter(moment.unix(state.reservation.eventDay));
        } else {
          state.loadingError = "Reservation does not exist."
        }
        state.loading = false;
        return;
      },
      [loadReservation.pending]: (state, action) => {
        state.loading = true;
        return;
      },
      [loadReservation.rejected]: (state, action) => {
        state.loadingError = action.payload;
        state.loading = false;
        return;
      },
      [changeDate.fulfilled]: (state, action) => {
        state.reservation.eventDate = action.payload.updateData.eventDate;
        state.reservation.eventDay = action.payload.updateData.eventDay;
        state.reservation.allDay = action.payload.updateData.allDay;
        state.reservation.timeStarts = action.payload.updateData.starts;
        state.reservation.timeEnds = action.payload.updateData.ends;
        state.reservation.shortDate = moment(action.payload.updateData.eventDate,constants.dayFormat).format('ll');
        state.reservation.hours = action.payload.updateData.allDay?'All Day':moment(action.payload.updateData.starts).format('h:mm a') + ' to ' + moment(action.payload.updateData.ends).format('h:mm a z');
        if(state.reservation.eventID){
          state.reservation.event={
            ...state.reservation.event,
            starts:action.payload.updateData.starts,
            ends:action.payload.updateData.ends,
            allDay:action.payload.updateData.allDay
          };
        }
        state.changingDate = false;
        state.editingDate = false;
        return;
      },
      [changeDate.pending]: (state, action) => {
        state.changeDateError = null;
        state.changingDate = true;
        return;
      },
      [changeDate.rejected]: (state, action) => {
        state.changeDateError = action.payload;
        state.changingDate = false;
        return;
      },
      [changeLocation.fulfilled]: (state, action) => {
        state.reservation.location={...action.payload.location};
        if(!state.reservation.eventID){
          state.reservation.event={
            ...state.reservation.event,
            location:{...action.payload.location}
          };
        }
        state.changingLocation = false;
        state.editingLocation = false;
        return;
      },
      [changeLocation.pending]: (state, action) => {
        state.changeLocationError = null;
        state.changingLocation = true;
        return;
      },
      [changeLocation.rejected]: (state, action) => {
        state.changeLocationError = action.payload;
        state.changingLocation = false;
        return;
      },
      [changeEventType.fulfilled]: (state, action) => {
        state.reservation.eventID = action.payload.id;
        state.reservation.event = action.payload;
        state.changingEventType = false;
        state.editingEventType = false;
        return;
      },
      [changeEventType.pending]: (state, action) => {
        state.changeEventTypeError = null;
        state.changingEventType = true;
        return;
      },
      [changeEventType.rejected]: (state, action) => {
        state.changeEventTypeError = action.payload;
        state.changingEventType = false;
        return;
      },
      [loadLocations.fulfilled]: (state, action) => {
        state.locations = action.payload;
        state.loadingLocations = false;
        return;
      },
      [loadLocations.pending]: (state, action) => {
        state.loadingLocations = true;
        return;
      },
      [loadLocations.rejected]: (state, action) => {
        state.changeLocationError = action.payload;
        state.loadingLocations = false;
        return;
      },
      [loadEventTypes.fulfilled]: (state, action) => {
        state.eventTypes = action.payload;
        state.loadingEventTypes = false;
        return;
      },
      [loadEventTypes.pending]: (state, action) => {
        state.loadingEventTypes = true;
        return;
      },
      [loadEventTypes.rejected]: (state, action) => {
        state.changeEventTypeError = action.payload;
        state.loadingEventTypes = false;
        return;
      },
      [changePayout.fulfilled]: (state, action) => {
        state.reservation.event={...action.payload};
        state.changingPayout = false;
        state.editingPayout = false;
        return;
      },
      [changePayout.pending]: (state, action) => {
        state.changePayoutError = null;
        state.changingPayout = true;
        return;
      },
      [changePayout.rejected]: (state, action) => {
        state.changePayoutError = action.payload;
        state.changingPayout = false;
        return;
      },
      [changePaymentTo.fulfilled]: (state, action) => {
        state.reservation.paymentTo={...action.payload.paymentTo};
        state.changingPaymentTo = false;
        state.editingPaymentTo = false;
        return;
      },
      [changePaymentTo.pending]: (state, action) => {
        state.changePaymentToError = null;
        state.changingPaymentTo = true;
        return;
      },
      [changePaymentTo.rejected]: (state, action) => {
        state.changePaymentToError = action.payload;
        state.changingPaymentTo = false;
        return;
      },
      [changeStatus.fulfilled]: (state, action) => {
        state.reservation = {
          ...state.reservation,
          ...action.payload
        };
        state.reservation.filterStatus = reservationStatus({...state.reservation});
        state.changingStatus = false;
        state.activeModal = null;
        return;
      },
      [changeStatus.pending]: (state, action) => {
        state.changeStatusError = null;
        state.changingStatus = true;
        return;
      },
      [changeStatus.rejected]: (state, action) => {
        state.changeStatusError = action.payload;
        state.changingStatus = false;
        return;
      },
      [saveResults.fulfilled]: (state, action) => {
        state.reservation = {
          ...state.reservation,
          ...action.payload,
          filterStatus:(action.payload.thresholdNotMet?'Completed':'Pending Payment')
        };
        state.savingResults = false;
        return;
      },
      [saveResults.pending]: (state, action) => {
        state.savingResultsError = null;
        state.savingResults = true;
        return;
      },
      [saveResults.rejected]: (state, action) => {
        state.savingResultsError = action.payload;
        state.savingResults = false;
        return;
      },
      [savePayment.fulfilled]: (state, action) => {
        state.reservation.payment = true;
        state.reservation.filterStatus = 'Completed';
        state.savingPayment = false;
        return;
      },
      [savePayment.pending]: (state, action) => {
        state.savingPaymentError = null;
        state.savingPayment = true;
        return;
      },
      [savePayment.rejected]: (state, action) => {
        state.savingPaymentError = action.payload;
        state.savingPayment = false;
        return;
      },
      [loadMessages.fulfilled]: (state, action) => {
        state.messages = action.payload;
        state.loadingMessages=false;
        return;
      },
      [loadMessages.pending]: (state, action) => {
        state.messagesError = null;
        state.loadingMessages=true;
        return;
      },
      [loadMessages.rejected]: (state, action) => {
        state.messagesError = action.payload;
        state.loadingMessages=false;
        return;
      },
      [loadNotes.fulfilled]: (state, action) => {
        state.notes = action.payload;
        state.loadingNotes = false;
        return;
      },
      [loadNotes.pending]: (state, action) => {
        state.notesError = null;
        state.loadingNotes = true;
        return;
      },
      [loadNotes.rejected]: (state, action) => {
        state.notesError = action.payload;
        state.loadingNotes = false;
        return;
      },
      [saveNote.fulfilled]: (state, action) => {
        state.notes.unshift(action.payload);
        state.savingNote = false;
        state.savingNoteError = null;
        return;
      },
      [saveNote.pending]: (state, action) => {
        state.savingNoteError = null;
        state.savingNote = true;
        return;
      },
      [saveNote.rejected]: (state, action) => {
        state.savingNoteError = action.payload;
        state.savingNote = false;
        return;
      },
      [updateNote.fulfilled]: (state, action) => {
        const updated = action.payload;
        state.notes = state.notes.map(n => n.id === updated.id ? updated : n);
        state.updatingNoteID = null;
        state.updatingNoteError = null;
        return;
      },
      [updateNote.pending]: (state, action) => {
        state.updatingNoteID = action.meta.arg.noteID;
        state.updatingNoteError = null;
        return;
      },
      [updateNote.rejected]: (state, action) => {
        state.updatingNoteID = null;
        state.updatingNoteError = action.payload?.message || action.payload;
        return;
      },
      [deleteNote.fulfilled]: (state, action) => {
        state.notes = state.notes.filter(n => n.id !== action.payload);
        state.deletingNoteID = null;
        state.deletingNoteError = null;
        return;
      },
      [deleteNote.pending]: (state, action) => {
        state.deletingNoteID = action.meta.arg;
        state.deletingNoteError = null;
        return;
      },
      [deleteNote.rejected]: (state, action) => {
        state.deletingNoteID = null;
        state.deletingNoteError = action.payload?.message || action.payload;
        return;
      },
      [refreshCreative.fulfilled]: (state, action) => {
        state.refreshingCreative=false;
        return;
      },
      [refreshCreative.pending]: (state, action) => {
        state.creativeError = null;
        state.refreshingCreative=true;
        return;
      },
      [refreshCreative.rejected]: (state, action) => {
        state.creativeError = action.payload;
        state.refreshingCreative=false;
        return;
      },
      [loadEmails.fulfilled]: (state, action) => {
        state.emails = Array.isArray(action.payload)?action.payload:[];
        state.loadingEmails=false;
        return;
      },
      [loadEmails.pending]: (state, action) => {
        state.emailsError = null;
        state.loadingEmails=true;
        return;
      },
      [loadEmails.rejected]: (state, action) => {
        state.emailsError = action.payload;
        state.loadingEmails=false;
        return;
      },
      [resendEmail.fulfilled]: (state, action) => {
        let m = action.payload;
        // `emailToResend` is cleared at the end of this reducer, so a second fulfilled
        // action (a double-click, or a stale in-flight request) would dereference null
        // and throw inside the reducer. Read it defensively; the row still renders with
        // whatever the payload carries.
        state.emails.unshift({
          EmailID:`resend-${m.MessageID}`,
          MessageID:m.MessageID,
          Type:'Processed',
          Recipient:state.emailToResend?.Recipient||m.To||null,
          Subject:state.emailToResend?.Subject||'',
          ReceivedAt:moment(m.SubmittedAt).unix(),
          Events:[{
            MessageID:m.MessageID,
            Type:'Processed',
            Recipient:state.emailToResend?.Recipient||m.To||null,
            ReceivedAt:moment(m.SubmittedAt).unix(),
          }],
        });
        state.emailToResend=null;
        state.loadingEmails=false;
        return;
      },
      [resendEmail.pending]: (state, action) => {
        state.resendError = null;
        return;
      },
      [resendEmail.rejected]: (state, action) => {
        // NOT `emailsError` — see the initialState comment. That field blanks the table.
        state.resendError = action.payload;
        // Release the per-row spinner, which is derived from `emailToResend`; leaving it
        // set kept the failed row spinning indefinitely.
        state.emailToResend = null;
        return;
      },
      [loadEmail.fulfilled]: (state, action) => {
        state.emailToLoad=action.payload;
        state.loadingEmail=false;
        return;
      },
      [loadEmail.pending]: (state, action) => {
        state.emailToLoad = null;
        state.loadEmailError = null;
        state.loadingEmail=true;
        return;
      },
      [loadEmail.rejected]: (state, action) => {
        state.emailToLoad = null;
        state.loadEmailError = action.payload;
        state.loadingEmail=false;
        return;
      },
      [getPurchases.fulfilled]: (state, action) => {
        state.purchases = action.payload;
        state.loadingPurchases=false;
        return;
      },
      [getPurchases.pending]: (state, action) => {
        state.purchasesError = null;
        state.loadingPurchases=true;
        return;
      },
      [getPurchases.rejected]: (state, action) => {
        console.log(action.payload)
        state.purchasesError = action.payload;
        state.loadingPurchases=false;
        return;
      },
      [fetchOrders.fulfilled]: (state, action) => {
        state.orders = action.payload;
        state.loadingOrders = false;
        return;
      },
      [fetchOrders.pending]: (state, action) => {
        state.ordersError = null;
        // A location drill-down is loaded on demand. Clear the previous location's rows
        // while the next query runs so they cannot flash under the wrong modal title.
        state.orders = [];
        state.loadingOrders = true;
        return;
      },
      [fetchOrders.rejected]: (state, action) => {
        state.ordersError = action.payload;
        state.loadingOrders = false;
        return;
      },
      [loadImportResults.fulfilled]: (state, action) => {
        state.importResults = action.payload;
        state.loadingImportResults = false;
        return;
      },
      [loadImportResults.pending]: (state, action) => {
        state.importResultsError = null;
        state.loadingImportResults = true;
        return;
      },
      [loadImportResults.rejected]: (state, action) => {
        state.importResultsError = action.payload;
        state.loadingImportResults = false;
        return;
      }
    }
  });

  export default reservationSlice.reducer;
  export const {
    setEditingDate,
    setEditingEventType,
    setChangeEventTypeError,
    setChangeDateError,
    setEditingLocation,
    setChangeLocationError,
    setChangePayoutError,
    setEditingPayout,
    setEditingPaymentTo,
    setChangePaymentToError,
    setActiveModal,
    setChangeStatusText,
    setChangeStatusError,
    setSavingResultsError,
    setSavingPaymentError,
    setMessages,
    setNotes,
    setUpdatingNoteError,
    setDeletingNoteError,
    setEmailToResend,
    setResendError,
    setEmailToLoad,
    setAchFrom
  } = reservationSlice.actions;
